import { LoaderFunctionArgs } from '@remix-run/node';
import { Link, useSearchParams } from '@remix-run/react';
import CustomPagination from '~/components/custom-pagination';
import Layout from '~/components/layout';
import StarRating from '~/components/star-rating';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '~/components/ui/breadcrumb';
import { Button } from '~/components/ui/button';
import { calcAuctionHouseRatingFromAuctionReviews } from '~/lib/utils';
import { getAuctionHouseList } from '~/services/api/auction-houses';
import { validateUser } from '~/services/api/auth';
import { Home } from 'lucide-react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { user } = await validateUser(request);

  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || 1;

  const auctionHouseData = await getAuctionHouseList({
    limit: limit,
    page: page,
  });

  return typedjson({
    user,
    auctionHouseData,
    totalPages: Math.ceil(auctionHouseData.totalCount / limit),
    currentPage: page,
    limit,
  });
};

export default function AuctionHouses() {
  const data = useTypedLoaderData<typeof loader>();

  const [searchParams, setSearchParams] = useSearchParams();

  const { list, totalCount } = data.auctionHouseData;

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <Breadcrumb className="mt-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/" className="flex flex-row items-center gap-1 text-sm font-normal">
              <Home className="h-5 w-5" />
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbLink>
            <BreadcrumbLink href="/auctions" className="text-sm font-normal">
              Auction Houses
            </BreadcrumbLink>
          </BreadcrumbLink>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-2 mt-4 flex flex-col">
        <h1 className="text-2xl font-semibold">Auction Houses</h1>
        <span className="text-sm text-slate-600">{list.length} auction houses found</span>
      </div>
      <hr className="mb-4 h-[1px] bg-slate-200" />
      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
        {list.map((auctionHouse) => {
          const reviews = auctionHouse.auctions.flatMap((a) => a.reviews);

          return (
            <div key={auctionHouse.id} className={`flex w-full flex-col items-center gap-3 rounded-lg border py-4`}>
              <img
                src={auctionHouse.image}
                alt={`${auctionHouse.name}`}
                className="h-20 w-20 rounded-lg border object-contain"
              />
              <div className="flex flex-col items-center gap-2 px-[5%]">
                <p className="rounded-full bg-slate-200 px-2 py-1 text-xs font-normal capitalize">
                  {auctionHouse.type}
                </p>
                <p className="font-semibold text-black">{auctionHouse.name}</p>
                <p className="text-sm text-slate-500">{auctionHouse.auctions.length} auctions found</p>
                <div className="flex flex-row items-center gap-2">
                  <StarRating rating={calcAuctionHouseRatingFromAuctionReviews(reviews)} size={16} />
                  <p className="text-xs font-normal text-[#646464]">{reviews.length} Reviews</p>
                </div>
                <div className="mt-2 w-full">
                  <Link to={`/auction-house/${auctionHouse.slug}`}>
                    <Button variant={'outline'} className="w-full border-black" size="sm">
                      Explore
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-10 mt-4">
        <CustomPagination
          limit={data.limit}
          totalCount={totalCount}
          totalPages={data.totalPages}
          currentPage={data.currentPage || 1}
          onPrevPage={() => {
            searchParams.set('page', (data.currentPage - 1).toString());
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onNextPage={() => {
            searchParams.set('page', (data.currentPage + 1).toString());
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onPageChange={(page) => {
            searchParams.set('page', page);
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
          onLimitChange={(limit) => {
            searchParams.set('limit', limit);
            setSearchParams(searchParams, {
              preventScrollReset: true,
            });
          }}
        />
      </div>
    </Layout>
  );
}
